import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

export interface KeyStageOption { id: string; name: string; }
export interface YearGroupOption { id: string; name: string; key_stage_id: string; }
export interface ClassOption { id: string; name: string; year_group_id: string; }

export interface SchoolFilters {
  keyStages: KeyStageOption[];
  yearGroups: YearGroupOption[];
  classes: ClassOption[];
}

export function useSchoolFilters(schoolId: string | null | undefined) {
  return useQuery<SchoolFilters>({
    queryKey: ['school-filters', schoolId],
    enabled: !!schoolId,
    staleTime: 5 * 60_000,
    queryFn: async () => {
      const [ksResult, ygResult] = await Promise.all([
        supabase.from('key_stages').select('id, name').eq('school_id', schoolId!),
        supabase.from('year_groups').select('id, name, key_stage_id'),
      ]);
      if (ksResult.error) throw ksResult.error;
      if (ygResult.error) throw ygResult.error;

      const ksIds = new Set((ksResult.data ?? []).map((k) => k.id));
      const filteredYgs = (ygResult.data ?? []).filter((yg) => ksIds.has(yg.key_stage_id));
      const ygIds = filteredYgs.map((yg) => yg.id);

      if (ygIds.length === 0) {
        return { keyStages: ksResult.data ?? [], yearGroups: filteredYgs, classes: [] };
      }

      const clResult = await supabase
        .from('classes')
        .select('id, name, year_group_id')
        .in('year_group_id', ygIds);
      if (clResult.error) throw clResult.error;

      return {
        keyStages: ksResult.data ?? [],
        yearGroups: filteredYgs,
        classes: clResult.data ?? [],
      };
    },
  });
}
